import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

// Widgets
import FleetWidget from '../widgets/FleetWidget';
import EdgeDevicesWidget from '../widgets/EdgeDevicesWidget';
import IoTGatewaysWidget from '../widgets/IoTGatewaysWidget';
import QuickAccessWidget from '../widgets/QuickAccessWidget';

export default function MainView({ slugs }) {
  const user = useSelector((state) => state.user.currentUser);
  const devices = useSelector((state) => state.assets.devices);
  const { t } = useTranslation();
  const direction = user?.lang === 'ar' || user?.lang === 'he' ? 'rtl' : 'ltr';
  const [loading, setLoading] = useState(true);
  
  const isSmall = window.innerWidth < 640;
  const isMedium = window.innerWidth < 768;
  
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={cn(
      "relative overflow-auto",
      "md:pb-14"
    )}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="p-6 sm:p-8"
        style={{ direction }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="p-6 sm:p-8 mb-6 rounded-3xl bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
            <div className={cn(
              "flex items-center gap-4",
              isSmall ? "flex-col items-start" : "flex-row items-center"
            )}>
              <Avatar className={cn(
                "border-2 border-white",
                isSmall ? "w-12 h-12" : "w-16 h-16"
              )}>
                <AvatarImage 
                  src={user?.photoURL || ''} 
                  alt={user?.displayName || 'User'} 
                />
                <AvatarFallback className="bg-blue-400 text-white">
                  {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div>
                <h1 className={cn(
                  "font-bold mb-2",
                  isSmall ? "text-2xl" : "text-4xl"
                )}>
                  {t('dashboard.welcome')}, {user?.displayName || user?.email?.split('@')[0] || 'User'}
                </h1>
                <p className="text-xl text-blue-100">
                  {t('dashboard.overview')}
                </p>
              </div>
            </div>
          </div>

          {/* Widgets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={item}>
              <FleetWidget loading={loading} devices={devices} />
            </motion.div>
            
            <motion.div variants={item}>
              <EdgeDevicesWidget loading={loading} />
            </motion.div>
            
            <motion.div variants={item}>
              <IoTGatewaysWidget loading={loading} />
            </motion.div>
            
            <motion.div variants={item}>
              <QuickAccessWidget loading={loading} />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
} 